import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Input,
  message,
  Modal,
  Spin,
  Tooltip,
  Card,
  Tag,
  Avatar,
  Select,
  Space,
  Drawer,
  Descriptions,
  Upload,
  Typography,
  Tabs,
  Row,
  Col,
  Divider,
  Badge,
  Statistic,
} from "antd";
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
  PaperClipOutlined,
  SendOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilterOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import moment from "moment";
import ComplaintAssignmentPanel from "./ComplaintAssignmentPanel";

const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;
const { Title, Text, Paragraph } = Typography;

const STATUS_CONFIG = {
  pending: { color: "gold", bg: "#fffbeb", border: "#fde68a", text: "#92400e", label: "Pending", icon: <ClockCircleOutlined /> },
  "in-progress": { color: "blue", bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", label: "In Progress", icon: <SyncOutlined spin /> },
  resolved: { color: "green", bg: "#f0fdf4", border: "#bbf7d0", text: "#166534", label: "Resolved", icon: <CheckCircleOutlined /> },
  rejected: { color: "red", bg: "#fff1f2", border: "#fecdd3", text: "#9f1239", label: "Rejected", icon: <ExclamationCircleOutlined /> },
};

const PRIORITY_CONFIG = {
  low: { color: "default", bg: "#f8fafc", text: "#64748b" },
  medium: { color: "orange", bg: "#fff7ed", text: "#c2410c" },
  high: { color: "red", bg: "#fff1f2", text: "#be123c" },
  urgent: { color: "volcano", bg: "#fef2f2", text: "#991b1b" },
};

const CATEGORY_LABELS = {
  "product-quality": "Product Quality",
  delivery: "Delivery Issue",
  damaged: "Damaged Item",
  "wrong-item": "Wrong Item",
  "missing-parts": "Missing Parts",
  other: "Other",
};

function statusLabel(status) {
  return STATUS_CONFIG[status]?.label || (status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending");
}

function safeText(v, fallback = "N/A") {
  return v || fallback;
}

function normalizeCustomerInfo(customerInfo) {
  if (!customerInfo) return null;
  try {
    const parsed = typeof customerInfo === "string" ? JSON.parse(customerInfo) : customerInfo;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      name: parsed.name || parsed.fullName || parsed.customerName || parsed.firstName || "",
      email: parsed.email || parsed.emailAddress || parsed.mail || "",
      phone: parsed.phone || parsed.mobile || parsed.contact || "",
      company: parsed.company || "",
      postalAddress: parsed.postalAddress || parsed.address || "",
      otherInfo: parsed.otherInfo || "",
    };
  } catch {
    return null;
  }
}

function normalizeComplaint(record) {
  const parsedCustomer = normalizeCustomerInfo(record.customerInfo || record.customer || null);
  const userObj = record.user
    ? {
        id: record.user.id,
        name: record.user.full_name || record.user.name || record.user.company_name || record.user.email || "",
        company: record.user.company_name || record.user.company || "",
        email: record.user.email || "",
        phone: record.user.phone || "",
      }
    : parsedCustomer
    ? { id: null, name: parsedCustomer.name || "", company: parsedCustomer.company || "", email: parsedCustomer.email || "", phone: parsedCustomer.phone || "" }
    : null;
  return { ...record, customerInfo: parsedCustomer, user: userObj };
}

function getUserFromRecord(record) {
  if (!record) return null;
  if (record.user) return record.user;
  const parsed = normalizeCustomerInfo(record.customerInfo || record.customer || null);
  return parsed ? { id: null, name: parsed.name || "", company: parsed.company || "", email: parsed.email || "", phone: parsed.phone || "" } : null;
}

function getAttachmentUrl(att) {
  return typeof att === "string" ? att : att?.url;
}

function getAttachmentName(att, idx) {
  if (typeof att === "string") {
    try {
      const raw = att.split("?")[0];
      const name = decodeURIComponent(raw.split("/").pop() || "");
      return name || `Attachment ${idx + 1}`;
    } catch {
      return `Attachment ${idx + 1}`;
    }
  }
  return att?.name || `Attachment ${idx + 1}`;
}

// ─── STAT CARD ─────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, bg }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        padding: "20px 24px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 4, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

// ─── STATUS TAG ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.text,
        borderRadius: 20,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ─── PRIORITY TAG ──────────────────────────────────────────────────────────
function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: cfg.bg,
        color: cfg.text,
        borderRadius: 20,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        border: `1px solid ${cfg.bg}`,
        textTransform: "capitalize",
      }}
    >
      {priority || "—"}
    </span>
  );
}

export default function AdminComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // ✅ FIX: track active tab

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ status: "all", priority: "all", sort: "newest" });

  const [stats, setStats] = useState({ total: 0, pending: 0, "in-progress": 0, resolved: 0, rejected: 0 });

  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [fileList, setFileList] = useState([]);

  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [resolutionText, setResolutionText] = useState("");

  const [staffList, setStaffList] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // ─── FETCHERS ────────────────────────────────────────────────────────────
  const fetchStaff = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/complaints/admin/staff");
      if (res.data?.success) setStaffList(res.data.staff || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/complaints/stats/overview");
      if (res.data?.success) {
        const s = res.data.stats?.overall || res.data.stats || {};
        setStats({ total: s.total || 0, pending: s.pending || 0, "in-progress": s["in-progress"] || 0, resolved: s.resolved || 0, rejected: s.rejected || 0 });
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.priority !== "all" && { priority: filters.priority }),
        ...(searchText && { search: searchText }),
        sort: filters.sort,
      };
      const res = await axiosInstance.get("/complaints/admin/all", { params });
      if (res.data?.success) {
        const normalized = (res.data.complaints || []).map((c) => normalizeComplaint(c));
        setComplaints(normalized);
        setPagination((prev) => ({ ...prev, total: res.data.pagination?.total || 0 }));
        if (res.data.stats) {
          const s = res.data.stats;
          setStats({ total: s.total || 0, pending: s.pending || 0, "in-progress": s["in-progress"] || 0, resolved: s.resolved || 0, rejected: s.rejected || 0 });
        }
      } else {
        setComplaints([]);
        message.error("Failed to load complaints");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred while fetching complaints.");
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters, searchText]);

  useEffect(() => { fetchStaff(); fetchStats(); }, [fetchStaff, fetchStats]);
  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  const refreshSelectedComplaint = useCallback(async (id) => {
    try {
      const res = await axiosInstance.get(`/complaints/admin/${id}`);
      if (res.data?.success) {
        const normalized = normalizeComplaint(res.data.complaint || {});
        setSelectedComplaint(normalized);
      }
    } catch (err) {
      console.error("Error refreshing complaint:", err);
    }
  }, []);

  const openComplaint = async (record) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/complaints/admin/${record.id}`);
      if (res.data?.success) {
        const normalized = normalizeComplaint(res.data.complaint || {});
        setSelectedComplaint(normalized);
        setDetailDrawerVisible(true);
      } else {
        message.error("Failed to load complaint details");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading complaint details");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) { message.warning("Please enter a message"); return; }
    if (!selectedComplaint?.id) { message.error("No complaint selected"); return; }
    try {
      setSendingMessage(true);
      const formData = new FormData();
      formData.append("message", messageText.trim());
      fileList.forEach((file) => { if (file?.originFileObj) formData.append("attachments", file.originFileObj); });
      const res = await axiosInstance.post(`/complaints/admin/${selectedComplaint.id}/messages`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.success) {
        message.success("Reply sent successfully");
        setMessageText(""); setFileList([]);
        await refreshSelectedComplaint(selectedComplaint.id);
        fetchComplaints(); fetchStats();
      } else {
        message.error(res.data?.message || "Failed to send message");
      }
    } catch (err) {
      console.error(err); message.error("Network error while sending message");
    } finally {
      setSendingMessage(false);
    }
  };

  const updateStatus = async (id, status, resolution = "") => {
    if (!id || !status) return;
    if (status === "resolved" && !resolution.trim()) { message.warning("Please enter resolution details"); return; }
    try {
      setLoading(true);
      const res = await axiosInstance.put(`/complaints/admin/${id}/status`, { status, ...(resolution?.trim() && { resolution: resolution.trim() }) });
      if (res.data?.success) {
        message.success(`Status updated to ${statusLabel(status)}`);
        setStatusModalVisible(false); setResolutionText(""); setNewStatus("");
        fetchComplaints(); fetchStats();
        if (detailDrawerVisible) await refreshSelectedComplaint(id);
      } else {
        message.error(res.data?.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err); message.error("Network error while updating status");
    } finally {
      setLoading(false);
    }
  };

  const assignComplaint = async ({ assignedToUserId, note }) => {
    if (!selectedComplaint?.id) { message.error("No complaint selected"); return; }
    try {
      setAssignLoading(true);
      const res = await axiosInstance.post(`/complaints/admin/${selectedComplaint.id}/assign`, { assignedToUserId, note: note || "" });
      if (res.data?.success) {
        message.success("Assignment saved successfully");
        await refreshSelectedComplaint(selectedComplaint.id);
        fetchComplaints();
      } else {
        message.error(res.data?.message || "Failed to assign complaint");
      }
    } catch (err) {
      console.error(err); message.error("Network error while assigning complaint");
    } finally {
      setAssignLoading(false);
    }
  };

  const clearAssignment = async () => {
    await assignComplaint({ assignedToUserId: null, note: "Assignment cleared by admin" });
  };
  const inlineAssign = (record, staffId) => {
  const staff = staffList.find((s) => s.id === staffId);
  const staffName = staff?.full_name || staff?.name || staff?.email || "this staff member";
  confirm({
    title: `Assign to ${staffName}?`,
    icon: <TeamOutlined />,
    content: `Ticket #${record.id} will be assigned to ${staffName} (${staff?.role || ""}).`,
    okText: "Assign",
    cancelText: "Cancel",
    okButtonProps: { style: { background: "linear-gradient(135deg,#1e40af,#3b82f6)", border: "none" } },
    onOk: async () => {
      try {
        const res = await axiosInstance.post(`/complaints/admin/${record.id}/assign`, {
          assignedToUserId: staffId,
          note: "",
        });
        if (res.data?.success) {
          message.success("Assigned successfully");
          fetchComplaints();
        } else {
          message.error(res.data?.message || "Failed to assign");
        }
      } catch {
        message.error("Network error");
      }
    },
  });
};

  const handleDelete = (id) => {
    confirm({
      title: "Delete this complaint?",
      icon: <ExclamationCircleOutlined />,
      content: "This will permanently delete the complaint and all associated messages.",
      okText: "Delete", okType: "danger", cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const res = await axiosInstance.delete(`/complaints/admin/${id}`);
          if (res.data?.success) {
            message.success("Complaint deleted successfully");
            if (selectedComplaint?.id === id) { setDetailDrawerVisible(false); setSelectedComplaint(null); }
            fetchComplaints(); fetchStats();
          } else {
            message.error(res.data?.message || "Failed to delete complaint");
          }
        } catch (err) {
          console.error(err); message.error("Network error while deleting complaint");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
  };

  // ─── FILTERED & TABBED DATA ───────────────────────────────────────────────
  const filteredComplaints = useMemo(() => {
    return complaints.filter((c) => {
      if (filters.status !== "all" && c.status !== filters.status) return false;
      if (filters.priority !== "all" && c.priority !== filters.priority) return false;
      return true;
    });
  }, [complaints, filters.status, filters.priority]);

  // ✅ FIX: All tab views computed correctly
  const ticketsByView = useMemo(() => {
    const all = filteredComplaints;
    return {
      all,
      open: all.filter((c) => ["pending", "in-progress"].includes(c.status)),
      unassigned: all.filter((c) => !c.assignee?.id && !c.assignedToUserId),
      assigned: all.filter((c) => !!(c.assignee?.id || c.assignedToUserId)),
      resolved: all.filter((c) => c.status === "resolved"),
    };
  }, [filteredComplaints]);

  // ✅ FIX: Table data driven by active tab
  const tableData = useMemo(() => ticketsByView[activeTab] || ticketsByView.all, [ticketsByView, activeTab]);

  // ─── TABLE COLUMNS ─────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 70,
        render: (text) => (
          <span style={{ fontWeight: 700, color: "#3b82f6", fontSize: 13 }}>#{text}</span>
        ),
      },
      {
        title: "Customer",
        key: "user",
        render: (_, record) => {
          const u = getUserFromRecord(record) || {};
          const name = u?.name || u?.email || "N/A";
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Avatar
                size={36}
                icon={<UserOutlined />}
                style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{safeText(name)}</div>
                {u?.company && <div style={{ fontSize: 11, color: "#64748b" }}>{u.company}</div>}
                {u?.email && <div style={{ fontSize: 11, color: "#94a3b8" }}>{u.email}</div>}
              </div>
            </div>
          );
        },
      },
      {
        title: "Subject",
        dataIndex: "subject",
        key: "subject",
        ellipsis: true,
        render: (text) => (
          <span style={{ fontWeight: 500, color: "#1e293b", fontSize: 13 }}>{safeText(text)}</span>
        ),
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 140,
        render: (cat) => (
          <Tag
            style={{
              borderRadius: 20,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontWeight: 500,
              fontSize: 11,
            }}
          >
            {CATEGORY_LABELS[cat] || cat || "—"}
          </Tag>
        ),
      },
      {
        title: "Priority",
        dataIndex: "priority",
        key: "priority",
        width: 100,
        render: (priority) => <PriorityBadge priority={priority} />,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (status) => <StatusBadge status={status} />,
      },
   {
  title: "Assigned To",
  key: "assignedTo",
  width: 190,
  render: (_, record) => {
    const currentId = record.assignee?.id || record.assignedToUserId || null;
    const currentName = record.assignee?.name || record.assignee?.full_name || null;

    return (
      <Select
        size="small"
        allowClear
        showSearch
        placeholder="Unassigned"
        value={currentId || undefined}
        style={{ width: "100%", minWidth: 150 }}
        optionFilterProp="label"
        popupMatchSelectWidth={false}
        onClick={(e) => e.stopPropagation()}
        onChange={(staffId) => {
          if (!staffId) {
            confirm({
              title: "Clear assignment?",
              icon: <ExclamationCircleOutlined />,
              content: `Remove current assignee from Ticket #${record.id}?`,
              okText: "Clear",
              okType: "danger",
              cancelText: "Cancel",
              onOk: async () => {
                try {
                  const res = await axiosInstance.post(
                    `/complaints/admin/${record.id}/assign`,
                    {
                      assignedToUserId: null,
                      note: "Assignment cleared",
                    }
                  );
                  if (res.data?.success) {
                    message.success("Assignment cleared");
                    fetchComplaints();
                  } else {
                    message.error(res.data?.message || "Failed");
                  }
                } catch {
                  message.error("Network error");
                }
              },
            });
          } else {
            inlineAssign(record, staffId);
          }
        }}
        options={staffList.map((s) => ({
  value: s.id,
  label: `${s.full_name || s.name || s.email} — ${s.role || ""}`,
  name: s.full_name || s.name || s.email,
  role: s.role || "",
}))}

optionRender={(opt) => (
  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, padding: "2px 0" }}>
    <span style={{ fontWeight: 600, fontSize: 12, color: "#0f172a" }}>
      {opt.data.name}
    </span>
    <span style={{ fontSize: 11, color: "#6b7280" }}>
      {opt.data.role}
    </span>
  </div>
)}

labelRender={() =>
  currentName ? (
    <span style={{ fontWeight: 600, fontSize: 12, color: "#0f172a" }}>
      {currentName}
    </span>
  ) : (
    <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>
      Unassigned
    </span>
  )
}
        styles={{
          selector: {
            borderRadius: 8,
            border: currentId ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
            background: currentId ? "#f0fdf4" : "#fafafa",
            height: "auto",
            minHeight: 36,
            paddingTop: currentName ? 4 : 0,
            paddingBottom: currentName ? 4 : 0,
          },
        }}
      />
    );
  },
},
      {
        title: "Order",
        key: "order",
        width: 120,
        render: (_, record) => {
          const ref = record.order?.referenceNumber || record.order?.trackingId || null;
          return ref ? (
            <Tag style={{ borderRadius: 8, fontSize: 11, fontWeight: 500, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569" }}>
              {ref}
            </Tag>
          ) : (
            <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
          );
        },
      },
      {
        title: "Updated",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 110,
        render: (date) => (
          <span style={{ color: "#6b7280", fontSize: 12 }}>{date ? moment(date).fromNow() : "—"}</span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        align: "center",
        render: (_, record) => (
          <Space size={6}>
            <Tooltip title="View Ticket">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openComplaint(record)}
                style={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
              />
            </Tooltip>
            <Tooltip title="Reply">
              <Button
                size="small"
                icon={<MessageOutlined />}
                onClick={() => openComplaint(record)}
                style={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                style={{ borderRadius: 8 }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [staffList, inlineAssign]
  );

  // ─── CLOSE DRAWER ─────────────────────────────────────────────────────
  const closeDrawer = () => {
    setDetailDrawerVisible(false);
    setSelectedComplaint(null);
    setMessageText("");
    setFileList([]);
    setNewStatus("");
    setResolutionText("");
    setStatusModalVisible(false);
  };

  return (
    <Spin spinning={loading} size="large">
      <div
        style={{
          minHeight: "100vh",
          background: "#f1f5f9",
          padding: "28px 24px",
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}
      >
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>

          {/* ── PAGE HEADER ──────────────────────────────────────────── */}
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              padding: "24px 32px",
              marginBottom: 24,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 16,
                  background: "linear-gradient(135deg,#1e40af,#3b82f6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InboxOutlined style={{ color: "#fff", fontSize: 24 }} />
              </div>
              <div>
                <Title level={3} style={{ margin: 0, color: "#0f172a", fontWeight: 800 }}>
                  Support Tickets
                </Title>
                <Text style={{ color: "#64748b", fontSize: 14 }}>
                  Manage complaints, assignments, replies and status updates
                </Text>
              </div>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { fetchComplaints(); fetchStats(); }}
              style={{ borderRadius: 10, height: 40, fontWeight: 600 }}
            >
              Refresh
            </Button>
          </div>

          {/* ── STAT CARDS ───────────────────────────────────────────── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <StatCard icon={<FileTextOutlined />} label="Total Tickets" value={stats.total} color="#3b82f6" bg="#eff6ff" />
            <StatCard icon={<ClockCircleOutlined />} label="Pending" value={stats.pending} color="#d97706" bg="#fffbeb" />
            <StatCard icon={<SyncOutlined />} label="In Progress" value={stats["in-progress"]} color="#2563eb" bg="#eff6ff" />
            <StatCard icon={<CheckCircleOutlined />} label="Resolved" value={stats.resolved} color="#16a34a" bg="#f0fdf4" />
            <StatCard icon={<ExclamationCircleOutlined />} label="Rejected" value={stats.rejected} color="#dc2626" bg="#fff1f2" />
          </div>

          {/* ── FILTERS ──────────────────────────────────────────────── */}
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "20px 24px",
              marginBottom: 20,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <FilterOutlined style={{ color: "#3b82f6" }} />
              <Text strong style={{ color: "#374151", fontSize: 14 }}>Filters & Search</Text>
            </div>
            <Row gutter={[12, 12]}>
              <Col xs={24} lg={8}>
                <Input
                  placeholder="Search by subject, customer, order..."
                  value={searchText}
                  onChange={(e) => { setPagination((p) => ({ ...p, current: 1 })); setSearchText(e.target.value); }}
                  allowClear
                  style={{ borderRadius: 10, height: 40 }}
                  prefix={<span style={{ color: "#94a3b8" }}>🔍</span>}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Select
                  value={filters.status}
                  onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setFilters((f) => ({ ...f, status: v })); }}
                  style={{ width: "100%", borderRadius: 10 }}
                  placeholder="Status"
                >
                  <Option value="all">All Status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="in-progress">In Progress</Option>
                  <Option value="resolved">Resolved</Option>
                  <Option value="rejected">Rejected</Option>
                </Select>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Select
                  value={filters.priority}
                  onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setFilters((f) => ({ ...f, priority: v })); }}
                  style={{ width: "100%" }}
                  placeholder="Priority"
                >
                  <Option value="all">All Priority</Option>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Select
                  value={filters.sort}
                  onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setFilters((f) => ({ ...f, sort: v })); }}
                  style={{ width: "100%" }}
                >
                  <Option value="newest">Newest First</Option>
                  <Option value="oldest">Oldest First</Option>
                </Select>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Button
                  style={{ width: "100%", borderRadius: 10, height: 32 }}
                  onClick={() => { setSearchText(""); setFilters({ status: "all", priority: "all", sort: "newest" }); setPagination((p) => ({ ...p, current: 1 })); }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </div>

          {/* ── TABS + TABLE ─────────────────────────────────────────── */}
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {/* ✅ FIX: Tabs with activeKey state that drives table data */}
            <div style={{ borderBottom: "1px solid #e2e8f0", padding: "0 24px" }}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                style={{ marginBottom: 0 }}
                items={[
                  {
                    key: "all",
                    label: (
                      <span style={{ fontWeight: activeTab === "all" ? 700 : 500 }}>
                        All&nbsp;
                        <Badge count={ticketsByView.all.length} style={{ background: "#e2e8f0", color: "#475569", fontSize: 11 }} showZero />
                      </span>
                    ),
                  },
                  {
                    key: "open",
                    label: (
                      <span style={{ fontWeight: activeTab === "open" ? 700 : 500 }}>
                        Open&nbsp;
                        <Badge count={ticketsByView.open.length} style={{ background: "#dbeafe", color: "#1d4ed8", fontSize: 11 }} showZero />
                      </span>
                    ),
                  },
                  {
                    key: "unassigned",
                    label: (
                      <span style={{ fontWeight: activeTab === "unassigned" ? 700 : 500 }}>
                        Unassigned&nbsp;
                        <Badge count={ticketsByView.unassigned.length} style={{ background: "#fef3c7", color: "#92400e", fontSize: 11 }} showZero />
                      </span>
                    ),
                  },
                  {
                    key: "assigned",
                    label: (
                      <span style={{ fontWeight: activeTab === "assigned" ? 700 : 500 }}>
                        Assigned&nbsp;
                        <Badge count={ticketsByView.assigned.length} style={{ background: "#d1fae5", color: "#065f46", fontSize: 11 }} showZero />
                      </span>
                    ),
                  },
                  {
                    key: "resolved",
                    label: (
                      <span style={{ fontWeight: activeTab === "resolved" ? 700 : 500 }}>
                        Resolved&nbsp;
                        <Badge count={ticketsByView.resolved.length} style={{ background: "#d1fae5", color: "#065f46", fontSize: 11 }} showZero />
                      </span>
                    ),
                  },
                ]}
              />
            </div>

            {/* ✅ FIX: Table uses tableData (tab-filtered) */}
            <Table
              dataSource={tableData}
              columns={columns}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} tickets`,
                style: { padding: "12px 24px" },
              }}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              rowClassName={() => "complaint-row"}
              style={{ fontSize: 13 }}
            />
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          DETAIL DRAWER
      ════════════════════════════════════════════════════════════════ */}
      <Drawer
        title={null}
        width={860}
        open={detailDrawerVisible}
        onClose={closeDrawer}
        destroyOnClose
        zIndex={2100} 
        bodyStyle={{ padding: 0, background: "#f8fafc" }}
        headerStyle={{ display: "none" }}
      >
        {selectedComplaint && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

            {/* Drawer Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                padding: "20px 28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileTextOutlined style={{ color: "#fff", fontSize: 20 }} />
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>
                    Ticket #{selectedComplaint.id}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 2 }}>
                    {selectedComplaint.subject}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <Select
                  value={selectedComplaint.status || "pending"}
                  onChange={(value) => { setNewStatus(value); setStatusModalVisible(true); }}
                  style={{ width: 160 }}
                  size="middle"
                >
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="in-progress">🔄 In Progress</Option>
                  <Option value="resolved">✅ Resolved</Option>
                  <Option value="rejected">❌ Rejected</Option>
                </Select>
                <Button onClick={closeDrawer} style={{ borderRadius: 10, height: 36, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
                  Close
                </Button>
              </div>
            </div>

            {/* Drawer Body - two-column layout */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "24px 28px",
                display: "grid",
                gridTemplateColumns: "1fr 340px",
                gap: 20,
                alignItems: "start",
              }}
              className="drawer-grid"
            >
              {/* LEFT COLUMN */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Ticket Info */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
                    <FileTextOutlined style={{ color: "#3b82f6" }} />
                    <Text strong style={{ fontSize: 14, color: "#0f172a" }}>Ticket Details</Text>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {[
                        { label: "Category", value: CATEGORY_LABELS[selectedComplaint.category] || selectedComplaint.category || "—" },
                        { label: "Priority", value: <PriorityBadge priority={selectedComplaint.priority} /> },
                        { label: "Status", value: <StatusBadge status={selectedComplaint.status} /> },
                        { label: "Created", value: selectedComplaint.createdAt ? moment(selectedComplaint.createdAt).format("MMM D, YYYY HH:mm") : "—" },
                        { label: "Tracking ID", value: selectedComplaint.trackingId || "—" },
                        { label: "Order Ref", value: selectedComplaint.order?.referenceNumber || "—" },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
                    <UserOutlined style={{ color: "#3b82f6" }} />
                    <Text strong style={{ fontSize: 14, color: "#0f172a" }}>Customer</Text>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <Avatar
                        size={48}
                        icon={<UserOutlined />}
                        style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
                          {selectedComplaint.user?.name || selectedComplaint.customerInfo?.name || "N/A"}
                        </div>
                        {(selectedComplaint.user?.company || selectedComplaint.customerInfo?.company) && (
                          <div style={{ fontSize: 13, color: "#64748b" }}>
                            {selectedComplaint.user?.company || selectedComplaint.customerInfo?.company}
                          </div>
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6 }}>
                          {(selectedComplaint.user?.email || selectedComplaint.customerInfo?.email) && (
                            <a
                              href={`mailto:${selectedComplaint.user?.email || selectedComplaint.customerInfo?.email}`}
                              style={{ fontSize: 12, color: "#3b82f6", display: "flex", alignItems: "center", gap: 4 }}
                            >
                              <MailOutlined />
                              {selectedComplaint.user?.email || selectedComplaint.customerInfo?.email}
                            </a>
                          )}
                          {(selectedComplaint.user?.phone || selectedComplaint.customerInfo?.phone) && (
                            <a
                              href={`tel:${selectedComplaint.user?.phone || selectedComplaint.customerInfo?.phone}`}
                              style={{ fontSize: 12, color: "#3b82f6", display: "flex", alignItems: "center", gap: 4 }}
                            >
                              <PhoneOutlined />
                              {selectedComplaint.user?.phone || selectedComplaint.customerInfo?.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                    <Text strong style={{ fontSize: 14, color: "#0f172a" }}>📋 Description</Text>
                  </div>
                  <div style={{ padding: "16px 20px" }}>
                    <Paragraph style={{ margin: 0, color: "#374151", lineHeight: 1.7, fontSize: 14 }}>
                      {selectedComplaint.description || "No description provided."}
                    </Paragraph>
                  </div>
                </div>

                {/* Attachments */}
                {Array.isArray(selectedComplaint.attachments) && selectedComplaint.attachments.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                    <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                      <Text strong style={{ fontSize: 14, color: "#0f172a" }}>📎 Attachments</Text>
                    </div>
                    <div style={{ padding: "12px 20px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {selectedComplaint.attachments.map((att, index) => {
                        const url = getAttachmentUrl(att);
                        const name = getAttachmentName(att, index);
                        if (!url) return null;
                        return (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: 8,
                              padding: "6px 12px",
                              fontSize: 12,
                              color: "#1d4ed8",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                          >
                            <PaperClipOutlined />
                            {name}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Conversation */}
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
                    <MessageOutlined style={{ color: "#3b82f6" }} />
                    <Text strong style={{ fontSize: 14, color: "#0f172a" }}>Conversation</Text>
                    {selectedComplaint.messages?.length > 0 && (
                      <Tag style={{ borderRadius: 20, fontSize: 11, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", marginLeft: "auto" }}>
                        {selectedComplaint.messages.length} messages
                      </Tag>
                    )}
                  </div>

                  {/* Message Thread */}
                  <div
                    style={{
                      maxHeight: 320,
                      overflowY: "auto",
                      padding: "16px 20px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      background: "#f8fafc",
                    }}
                  >
                    {(!selectedComplaint.messages || selectedComplaint.messages.length === 0) ? (
                      <div style={{ textAlign: "center", color: "#9ca3af", padding: "24px 0", fontSize: 13 }}>
                        No messages yet. Start the conversation below.
                      </div>
                    ) : (
                      selectedComplaint.messages.map((msg, idx) => {
                        const isSupport = msg.sender === "support";
                        return (
                          <div
                            key={msg.id || idx}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: isSupport ? "flex-end" : "flex-start",
                            }}
                          >
                            <div
                              style={{
                                maxWidth: "85%",
                                background: isSupport ? "linear-gradient(135deg,#1e40af,#3b82f6)" : "#fff",
                                border: isSupport ? "none" : "1px solid #e2e8f0",
                                borderRadius: isSupport ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                                padding: "10px 14px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: isSupport ? "rgba(255,255,255,0.75)" : "#6b7280",
                                  marginBottom: 4,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 12,
                                }}
                              >
                                <span>{isSupport ? "Support Team" : "Customer"}</span>
                                <span>{msg.createdAt ? moment(msg.createdAt).format("MMM D, HH:mm") : ""}</span>
                              </div>
                              <div style={{ fontSize: 13, color: isSupport ? "#fff" : "#1e293b", lineHeight: 1.6 }}>
                                {msg.message}
                              </div>
                              {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {msg.attachments.map((att, aIdx) => {
                                    const url = getAttachmentUrl(att);
                                    const name = getAttachmentName(att, aIdx);
                                    if (!url) return null;
                                    return (
                                      <a
                                        key={aIdx}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 4,
                                          fontSize: 11,
                                          color: isSupport ? "rgba(255,255,255,0.9)" : "#3b82f6",
                                          background: isSupport ? "rgba(255,255,255,0.15)" : "#eff6ff",
                                          borderRadius: 6,
                                          padding: "3px 8px",
                                          textDecoration: "none",
                                        }}
                                      >
                                        <PaperClipOutlined />
                                        {name}
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Reply Box */}
                  <div style={{ padding: "14px 20px", borderTop: "1px solid #e2e8f0" }}>
                    <TextArea
                      placeholder="Type your reply here..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      rows={3}
                      style={{ borderRadius: 12, marginBottom: 10, fontSize: 13 }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <Upload fileList={fileList} onChange={({ fileList: fl }) => setFileList(fl)} beforeUpload={() => false} multiple maxCount={5}>
                        <Button icon={<PaperClipOutlined />} size="small" style={{ borderRadius: 8 }}>
                          Attach Files
                        </Button>
                      </Upload>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={sendingMessage}
                        onClick={sendMessage}
                        disabled={!messageText.trim()}
                        style={{
                          borderRadius: 10,
                          height: 38,
                          background: "linear-gradient(135deg,#1e40af,#3b82f6)",
                          border: "none",
                          fontWeight: 600,
                        }}
                      >
                        Send Reply
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Resolution (if resolved) */}
                {selectedComplaint.resolution && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: 16,
                      padding: "16px 20px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 16 }} />
                      <Text strong style={{ color: "#166534", fontSize: 14 }}>Resolution</Text>
                    </div>
                    <Paragraph style={{ margin: 0, color: "#166534", fontSize: 13, lineHeight: 1.6 }}>
                      {selectedComplaint.resolution}
                    </Paragraph>
                    {selectedComplaint.resolvedAt && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "#16a34a" }}>
                        Resolved on {moment(selectedComplaint.resolvedAt).format("MMM D, YYYY HH:mm")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN — Assignment Panel */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {/* ✅ FIX: Assignment panel now receives correct complaint with state sync */}
                <ComplaintAssignmentPanel
                  complaint={selectedComplaint}
                  staffList={staffList}
                  loading={assignLoading}
                  onAssign={assignComplaint}
                  onClear={clearAssignment}
                />
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* ── STATUS MODAL ────────────────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SyncOutlined style={{ color: "#3b82f6" }} />
            <span>Update Status to: <strong>{statusLabel(newStatus)}</strong></span>
          </div>
        }
        open={statusModalVisible}
        onCancel={() => { setStatusModalVisible(false); setNewStatus(""); setResolutionText(""); }}
        onOk={() => { if (!selectedComplaint?.id) return; updateStatus(selectedComplaint.id, newStatus, resolutionText); }}
        okText="Update Status"
        cancelText="Cancel"
        destroyOnClose
         zIndex={2200}
        okButtonProps={{ style: { background: "linear-gradient(135deg,#1e40af,#3b82f6)", border: "none", borderRadius: 8 } }}
      >
        {newStatus === "resolved" && (
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151" }}>
              Resolution Details <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <TextArea
              rows={4}
              placeholder="Describe how this complaint was resolved..."
              value={resolutionText}
              onChange={(e) => setResolutionText(e.target.value)}
              style={{ borderRadius: 10 }}
            />
          </div>
        )}
        {newStatus === "rejected" && (
          <div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600, color: "#374151" }}>
              Rejection Reason <span style={{ color: "#6b7280", fontWeight: 400 }}>(optional)</span>
            </label>
            <TextArea
              rows={3}
              placeholder="Reason for rejection..."
              value={resolutionText}
              onChange={(e) => setResolutionText(e.target.value)}
              style={{ borderRadius: 10 }}
            />
          </div>
        )}
        {(newStatus === "pending" || newStatus === "in-progress") && (
          <p style={{ color: "#6b7280", margin: 0 }}>
            Status will be changed to <strong>{statusLabel(newStatus)}</strong>. Continue?
          </p>
        )}
      </Modal>

      <style>{`
        .complaint-row:hover td {
          background: #f8fafc !important;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 2px solid #e2e8f0 !important;
          padding: 12px 16px !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 14px 16px !important;
          transition: background 0.15s;
        }
        .ant-tabs-tab {
          padding: 14px 4px !important;
          font-size: 13px !important;
        }
        .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        @media (max-width: 768px) {
          .drawer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </Spin>
  );
}
